import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Plus, Edit2, Clock, Coins, Share2 } from 'lucide-react';
import { listJobs, updateJobTitle, getJobStatus, getGuidelines } from '../features/contentCompliance/contentComplianceSlice';
import { selectIsAuthenticated } from '../features/auth/authSlice';
import youtubeIcon from '../assets/youtube_icon.png';
import tiktokIcon from '../assets/tiktok_icon.png';
import instagramIcon from '../assets/insta_icon.png';
import ScanReportBody from './report/ScanReportBody';
import ShareReportDialog from './report/ShareReportDialog';
import { track, scoreBand } from '../analytics/analytics';
import './HistoryPage.css';

// Facebook icon as inline SVG data URI (no PNG asset available)
const facebookIcon = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>')}`;

function HistoryPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [selectedJobData, setSelectedJobData] = useState(null);
  const [isLoadingJobDetails, setIsLoadingJobDetails] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  // Redux state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const jobsList = useSelector((state) => state.contentCompliance.jobsList);
  const jobsListStatus = useSelector((state) => state.contentCompliance.jobsListStatus);
  const jobsListError = useSelector((state) => state.contentCompliance.jobsListError);
  const jobsListCursor = useSelector((state) => state.contentCompliance.jobsListCursor);
  const jobsListHasMore = useSelector((state) => state.contentCompliance.jobsListHasMore);
  const jobsListTotalCount = useSelector((state) => state.contentCompliance.jobsListTotalCount);
  const guidelines = useSelector((state) => state.contentCompliance.guidelines);

  // Redirect anonymous users to new-check page (unless viewing a specific job)
  useEffect(() => {
    const jobId = searchParams.get('job');
    if (!isAuthenticated && !jobId) {
      navigate('/dashboard/new-check', { replace: true });
    }
  }, [isAuthenticated, navigate, searchParams]);

  // Fetch jobs on component mount (only for authenticated users)
  useEffect(() => {
    if (isAuthenticated && jobsListStatus === 'idle') {
      dispatch(listJobs({ limit: 10, sortOrder: 'desc' }));
    }
  }, [dispatch, jobsListStatus, isAuthenticated]);

  // Fetch guidelines on mount
  useEffect(() => {
    dispatch(getGuidelines('all'));
  }, [dispatch]);

  // Handle job ID from URL query param
  useEffect(() => {
    const jobId = searchParams.get('job');
    if (jobId && !selectedItem) {
      setSelectedItem(jobId);
      fetchJobDetails(jobId);
    }
  }, [searchParams]);

  const fetchJobDetails = async (jobId) => {
    setIsLoadingJobDetails(true);
    try {
      const result = await dispatch(getJobStatus(jobId)).unwrap();
      if (result?.data) {
        setSelectedJobData(result.data);
        if (result.data.status === 'COMPLETED') {
          track('report_viewed', {
            platform: result.data.platform || 'unknown',
            score_band: scoreBand(result.data.data?.overall_score),
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch job details:', error);
    } finally {
      setIsLoadingJobDetails(false);
    }
  };

  // Format date from ISO string to "DD Mon YYYY, HH:MM am/pm" in browser's local timezone
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Ensure UTC dates are properly marked with 'Z'
      // If the date string doesn't have timezone info, assume it's UTC
      let normalizedDateString = dateString.trim();
      
      // Check if it's an ISO date string without timezone indicator
      if (normalizedDateString.includes('T') && 
          !normalizedDateString.endsWith('Z') && 
          !normalizedDateString.match(/[+-]\d{2}:?\d{2}$/)) {
        // Append 'Z' to explicitly mark as UTC
        normalizedDateString = normalizedDateString + 'Z';
      }
      
      // Create date object - if input is UTC (with 'Z'), JavaScript automatically converts to local time
      const date = new Date(normalizedDateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString);
        return '';
      }
      
      // getDate(), getMonth(), getHours(), etc. automatically return LOCAL timezone values
      // These methods handle the UTC to local conversion automatically
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate(); // Local day
      const month = months[date.getMonth()]; // Local month  
      const year = date.getFullYear(); // Local year
      
      let hours = date.getHours(); // Local hours (already converted from UTC)
      const minutes = date.getMinutes(); // Local minutes (already converted from UTC)
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      const minutesStr = minutes < 10 ? '0' + minutes : minutes;
      
      return `${day} ${month} ${year}, ${hours}:${minutesStr} ${ampm}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '';
    }
  };

  // Get platform icon
  const getPlatformIcon = (platform) => {
    const platformLower = platform?.toLowerCase();
    if (platformLower === 'youtube') return youtubeIcon;
    if (platformLower === 'tiktok') return tiktokIcon;
    if (platformLower === 'instagram') return instagramIcon;
    if (platformLower === 'facebook') return facebookIcon;
    return youtubeIcon; // Default
  };

  // Transform job data to history item format
  const historyItems = (jobsList || []).map((job) => ({
    id: job.job_id,
    title: job.title || 'Untitled',
    date: formatDate(job.created_at || job.updated_at),
    score: job.data?.overall_score || null,
    platform: job.platform || 'youtube',
    // null/undefined = ran before credit enforcement existed, or enforcement
    // was off at the time (prod default) -- distinct from a real 0-cost scan.
    creditsCharged: job.credits_charged ?? null,
    isEditable: true,
    jobData: job
  }));

  const totalItems = jobsListTotalCount || 0;
  const showingItems = historyItems.length;

  const handleNewCheck = () => {
    navigate('/dashboard/new-check');
  };

  const handleItemClick = (item) => {
    setSelectedItem(item.id);
    setSearchParams({ job: item.id });
    fetchJobDetails(item.id);
  };

  // Group violations by category
  const groupViolationsByCategory = (violations) => {
    const grouped = {};
    violations.forEach((violation) => {
      if (!grouped[violation.category]) {
        grouped[violation.category] = [];
      }
      grouped[violation.category].push({ text: violation.text });
    });
    return grouped;
  };

  const handleEditClick = (e, item) => {
    e.stopPropagation();
    setEditingItem(item.id);
    setEditTitle(item.title);
  };

  const handleEditSave = async (e, item) => {
    e.stopPropagation();
    if (editTitle.trim() && editTitle !== item.title) {
      try {
        await dispatch(updateJobTitle({ 
          jobId: item.id, 
          title: editTitle.trim() 
        })).unwrap();
        setEditingItem(null);
        setEditTitle('');
      } catch (error) {
        console.error('Error updating job title:', error);
        // Optionally show error message to user
      }
    } else {
      setEditingItem(null);
      setEditTitle('');
    }
  };

  const handleEditCancel = (e) => {
    e.stopPropagation();
    setEditingItem(null);
    setEditTitle('');
  };

  const handleLoadMore = () => {
    if (jobsListHasMore && jobsListStatus !== 'loading') {
      dispatch(listJobs({ 
        limit: 10, 
        cursor: jobsListCursor, 
        sortOrder: 'desc',
        append: true 
      }));
    }
  };

  // Get selected job info from list
  const selectedJobInfo = historyItems.find(item => item.id === selectedItem);

  // Detailed Report View
  if (selectedItem && (selectedJobData || isLoadingJobDetails)) {
    const overallScore = selectedJobData?.data?.overall_score;
    const platform = selectedJobInfo?.platform || selectedJobData?.platform || 'youtube';
    const platformName = platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();
    const jobTitle = selectedJobInfo?.title || selectedJobData?.title || 'Untitled';

    return (
      <div className="history-page">
        {isLoadingJobDetails ? (
          <div className="history-loading">
            <div className="history-loading-spinner"></div>
            <p>Loading report...</p>
          </div>
        ) : selectedJobData?.status === 'COMPLETED' && overallScore != null ? (
          <div className="history-detail-report">
            {/* Report Header */}
            <div className="history-report-header">
              <div className="history-report-header-left">
                <h2 className="history-report-title">{jobTitle}</h2>
                <div className="history-report-platform">
                  <img
                    src={platform.toLowerCase() === 'youtube' ? youtubeIcon :
                         platform.toLowerCase() === 'tiktok' ? tiktokIcon :
                         platform.toLowerCase() === 'instagram' ? instagramIcon :
                         platform.toLowerCase() === 'facebook' ? facebookIcon : youtubeIcon}
                    alt={platformName}
                    className="history-report-platform-icon"
                  />
                  <span>{platformName}</span>
                </div>
                {selectedJobData?.credits_charged != null && (
                  <div className="history-report-credits">
                    <Coins size={14} className="history-report-credits-icon" aria-hidden="true" />
                    <span>
                      {selectedJobData.credits_charged > 0
                        ? `${selectedJobData.credits_charged} credits used`
                        : 'Free scan'}
                    </span>
                  </div>
                )}
              </div>
              <div className="history-report-header-right">
                {/* Sharing needs an account -- anonymous users see the sign-up banner instead */}
                {isAuthenticated && (
                  <button className="history-share-btn" onClick={() => setIsShareOpen(true)}>
                    <Share2 size={18} />
                    Share
                  </button>
                )}
                <button className="history-back-library-btn" onClick={() => navigate('/dashboard/videos1')}>
                  Back to My Library
                </button>
                <button className="history-start-scan-btn" onClick={() => navigate('/dashboard/new-check')}>
                  <Plus size={20} />
                  Check Content
                </button>
              </div>
            </div>

            <ScanReportBody
              data={selectedJobData?.data}
              platform={platform}
              guidelines={guidelines}
            />

            {isShareOpen && (
              <ShareReportDialog
                jobId={selectedItem}
                reportTitle={jobTitle}
                onClose={() => setIsShareOpen(false)}
              />
            )}

            {/* Sign up prompt for anonymous users */}
            {!isAuthenticated && (
              <div className="history-signup-banner">
                <div className="history-signup-content">
                  <h3>Save your analysis history</h3>
                  <p>Create a free account to save all your content checks and access them anytime.</p>
                  <button className="history-signup-btn" onClick={() => navigate('/signup')}>
                    Create Free Account
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : selectedJobData?.status === 'FAILED' ? (
          <div className="history-error">
            <p>This job failed to process. Please try running the check again.</p>
          </div>
        ) : (
          <div className="history-loading">
            <div className="history-loading-spinner"></div>
            <p>Processing... Results will appear when ready.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="history-page">
      {/* Header */}
      <div className="history-header">
        <h1 className="history-title">History</h1>
        <button className="history-new-check-btn" onClick={handleNewCheck}>
          <Plus size={18} />
          Check Content
        </button>
      </div>

      {/* History List */}
      {jobsListStatus === 'loading' && historyItems.length === 0 ? (
        <div className="history-loading">
          <div className="history-loading-spinner"></div>
          <p>Loading history...</p>
        </div>
      ) : jobsListError && historyItems.length === 0 ? (
        <div className="history-error">
          <p>Error loading history: {jobsListError}</p>
          <button className="history-retry-btn" onClick={() => dispatch(listJobs({ limit: 10, sortOrder: 'desc' }))}>
            Retry
          </button>
        </div>
      ) : historyItems.length === 0 ? (
        <div className="history-empty">
          <Clock size={48} className="history-empty-icon" />
          <p className="history-empty-text">No content checks yet</p>
          <p className="history-empty-subtext">Start by checking your first piece of content</p>
          <button className="history-empty-button" onClick={handleNewCheck}>
            <Plus size={18} />
            Check Content
          </button>
        </div>
      ) : (
        <>
          <div className="history-list">
            {historyItems.map((item) => (
              <div
                key={item.id}
                className={`history-item ${selectedItem === item.id ? 'selected' : ''}`}
                onClick={() => handleItemClick(item)}
              >
                <div className="history-item-icon">
                  <img src={getPlatformIcon(item.platform)} alt={item.platform} className="history-platform-icon" />
                </div>
                <div className="history-item-content">
                  {editingItem === item.id ? (
                    <div className="history-item-edit">
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleEditSave(e, item);
                          } else if (e.key === 'Escape') {
                            handleEditCancel(e);
                          }
                        }}
                        className="history-edit-input"
                        autoFocus
                      />
                      <div className="history-edit-actions">
                        <button
                          className="history-edit-save"
                          onClick={(e) => handleEditSave(e, item)}
                        >
                          Save
                        </button>
                        <button
                          className="history-edit-cancel"
                          onClick={handleEditCancel}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="history-item-title-row">
                        <h3 className="history-item-title">{item.title}</h3>
                        {item.isEditable && (
                          <button
                            type="button"
                            className="history-edit-btn"
                            onClick={(e) => handleEditClick(e, item)}
                            aria-label="Edit title"
                            title="Edit title"
                          >
                            <Edit2 size={16} className="history-edit-icon" />
                          </button>
                        )}
                      </div>
                      <div className="history-item-meta-row">
                        <p className="history-item-date">{item.date}</p>
                        {item.creditsCharged !== null && (
                          <span className="history-item-credits">
                            <Coins size={12} className="history-item-credits-icon" aria-hidden="true" />
                            {item.creditsCharged > 0 ? `${item.creditsCharged} credits` : 'Free'}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="history-item-score">
                  {item.score !== null ? (
                    <span className="history-score-value">{item.score}%</span>
                  ) : (
                    <span className="history-score-value empty">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Load More Section */}
          {jobsListHasMore && (
            <div className="history-load-more">
              <button 
                className="history-load-more-btn" 
                onClick={handleLoadMore}
                disabled={jobsListStatus === 'loading'}
              >
                {jobsListStatus === 'loading' ? 'Loading...' : 'Load More'}
              </button>
              <p className="history-load-more-text">
                Showing {showingItems} of {totalItems} items
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HistoryPage;
